import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'

// stores
import { useAccessStore } from '@/stores/AccessStore'
import type { AccessSelected } from '@/stores/AccessStore'

/**
 * Shared state for location selector modal
 * This ensures all components use the same modal state
 */
const locationSelectorModalIsOpen = ref(false)

/**
 * Composable for managing location selector functionality
 * Handles access selection state, filtering logic, and modal state
 * @returns Object containing modal state, access selection state, computed selectors, and methods
 */
export function useLocationSelector() {
  const accessStore = useAccessStore()

  // Use storeToRefs to make store state reactive in composable
  const { accessAllowed, accessSelected } = storeToRefs(accessStore)

  /**
   * Computed property that filters and processes access items based on selections
   * Handles visibility logic for manufacturers, groups, and dealerships based on parent selections
   * @returns Filtered and processed access items with selection state
   */
  const accessSelector = computed(() => {
    // Loop through all items in accessAllowed, and check if the value is present in accessSelected.
    const newArray: {
      regions: Array<{ id: number; name: string; isSelected: boolean;[key: string]: unknown }>
      manufacturers: Array<{ id: number; name: string; isSelected: boolean; isVisible?: boolean; regions?: number[];[key: string]: unknown }>
      groups: Array<{ id: number; name: string; isSelected: boolean; isVisible?: boolean; regions?: number[]; manufacturers?: number[];[key: string]: unknown }>
      dealerships: Array<{ id: number; name: string; isSelected: boolean; isVisible?: boolean; groupId?: number; manufacturers?: number[];[key: string]: unknown }>
    } = {
      regions: [],
      manufacturers: [],
      groups: [],
      dealerships: []
    }
    // Type assertion needed because accessAllowed structure isn't fully typed
    const allowed = accessAllowed.value as unknown as {
      regions: Array<{ id: number; name: string;[key: string]: unknown }>
      manufacturers: Array<{ id: number; name: string; regions: number[];[key: string]: unknown }>
      groups: Array<{ id: number; name: string; regions: number[]; manufacturers: number[];[key: string]: unknown }>
      dealerships: Array<{ id: number; name: string; groupId: number; manufacturers: number[];[key: string]: unknown }>
    }

    newArray.regions = allowed.regions.map(obj => {
      return {
        ...obj,
        isSelected: accessSelected.value.regions.includes(obj.id)
      }
    })
    newArray.manufacturers = allowed.manufacturers.map(obj => {
      return {
        ...obj,
        isSelected: accessSelected.value.manufacturers.includes(obj.id)
      }
    })
    newArray.groups = allowed.groups.map(obj => {
      return {
        ...obj,
        isSelected: accessSelected.value.groups.includes(obj.id)
      }
    })
    newArray.dealerships = allowed.dealerships.map(obj => {
      return {
        ...obj,
        isSelected: accessSelected.value.dealerships.includes(obj.id)
      }
    })

    allowed.manufacturers.forEach(man => {
      // Check if the manufacturer should be visible in the DOM or not
      const manWithVisible = man as typeof man & { isVisible?: boolean }
      manWithVisible.isVisible = man.regions.some((manRegion: number) => accessSelected.value.regions.includes(manRegion))
      const isSelected = accessSelected.value.manufacturers.includes(man.id)

      // If is Selected, and should not be visible, remove is selected
      if (isSelected && !manWithVisible.isVisible) {
        // Remove from accessSelected using store method
        accessStore.toggleItem('manufacturers', man.id, false)
      }
    })
    // Filter out any that isVisible=false
    newArray.manufacturers = allowed.manufacturers
      .filter(man => (man as typeof man & { isVisible?: boolean }).isVisible)
      .map(man => ({
        ...man,
        isSelected: accessSelected.value.manufacturers.includes(man.id),
        isVisible: true
      }))

    allowed.groups.forEach(group => {
      // Determine if region match
      const regionMatch = group.regions.some((region: number) => accessSelected.value.regions.includes(region))

      // Determine if manufacturer match; orgs with no franchised sites (only
      // multi-brand / used supermarkets) have no manufacturers, so the
      // manufacturer filter does not apply to them
      const manufacturerMatch =
        group.manufacturers.length === 0 ||
        group.manufacturers.some((man: number) => accessSelected.value.manufacturers.includes(man))

      const groupWithVisible = group as typeof group & { isVisible?: boolean }
      // Check both matches
      if (regionMatch && manufacturerMatch) {
        groupWithVisible.isVisible = true
      } else {
        groupWithVisible.isVisible = false
      }

      const isSelected = accessSelected.value.groups.includes(group.id)

      // If is Selected, and should not be visible, remove is selected
      if (isSelected && !groupWithVisible.isVisible) {
        accessStore.toggleItem('groups', group.id, false)
      }
    })

    // Filter out any that isVisible=false
    newArray.groups = allowed.groups
      .filter(group => (group as typeof group & { isVisible?: boolean }).isVisible)
      .map(group => ({
        ...group,
        isSelected: accessSelected.value.groups.includes(group.id),
        isVisible: true
      }))

    allowed.dealerships.forEach(dealership => {
      const dealershipGroup = dealership.groupId

      // Determine if manufacturer match; brandless dealerships (multi-brand /
      // used supermarkets) carry no manufacturer, so the filter does not apply
      const manufacturerMatch =
        dealership.manufacturers.length === 0 ||
        dealership.manufacturers.some((man: number) => accessSelected.value.manufacturers.includes(man))

      // Determine if group match
      const groupMatch = accessSelected.value.groups.includes(dealershipGroup)

      const dealershipWithVisible = dealership as typeof dealership & { isVisible?: boolean; isSelected?: boolean }
      // Check both matches
      if (groupMatch && manufacturerMatch) {
        dealershipWithVisible.isVisible = true
      } else {
        dealershipWithVisible.isVisible = false
      }
      const isSelected = accessSelected.value.dealerships.includes(dealership.id)

      // If is Selected, and should not be visible, remove is selected
      if (isSelected && !dealershipWithVisible.isVisible) {
        accessStore.toggleItem('dealerships', dealership.id, false)
        dealershipWithVisible.isSelected = false
      }
    })

    // Filter out any that isVisible=false
    newArray.dealerships = allowed.dealerships
      .filter(dealership => (dealership as typeof dealership & { isVisible?: boolean }).isVisible)
      .map(dealership => ({
        ...dealership,
        isSelected: accessSelected.value.dealerships.includes(dealership.id),
        isVisible: true
      }))

    return newArray
  })

  /**
   * Toggles an item in the access selection
   * @param {string} category - The category to toggle (regions, manufacturers, groups, dealerships)
   * @param {number} itemId - The ID of the item to toggle
   * @param {boolean} [value] - Optional value to set (true/false), if not provided, toggles the current state
   */
  function toggleItem(category: string, itemId: number, value?: boolean): void {
    type CategoryKey = keyof AccessSelected
    accessStore.toggleItem(category as CategoryKey, itemId, value)
  }

  /**
   * Checks if all items in a category are selected
   * @param {string} category - The category to check
   * @returns {boolean} - True if all items are selected
   */
  function areAllSelected(category: string): boolean {
    const selected = accessSelected.value[category as keyof typeof accessSelected.value]
    const selector = accessSelector.value[category as keyof typeof accessSelector.value]
    if (selected.length > 0 && Array.isArray(selector)) {
      return selector.every((item: { id: number }) => selected.includes(item.id))
    } else {
      return false
    }
  }

  /**
   * Selects or deselects all items in a category
   * @param {string} category - The category to select/deselect all items
   */
  function selectAll(category: string): void {
    const selector = accessSelector.value[category as keyof typeof accessSelector.value]
    if (!Array.isArray(selector)) return

    // If all items are already selected, unselect them
    if (areAllSelected(category)) {
      selector.forEach((item: { id: number }) => {
        toggleItem(category, item.id, false)
      })
    } else {
      // Otherwise, select all items
      const selected = accessSelected.value[category as keyof typeof accessSelected.value]
      selector.forEach((item: { id: number }) => {
        if (!selected.includes(item.id)) {
          toggleItem(category, item.id, true)
        }
      })
    }
  }

  /**
   * Saves the current access selection to localStorage
   * Note: The store automatically persists on changes, but this method ensures immediate save
   */
  function saveAccessSelection(): void {
    accessStore.persistToLocalStorage()
  }

  /**
   * Opens the location selector modal
   */
  function openLocationSelectorModal(): void {
    locationSelectorModalIsOpen.value = true
  }

  /**
   * Closes the location selector modal
   */
  function closeLocationSelectorModal(): void {
    locationSelectorModalIsOpen.value = false

    // Refresh the page
    window.location.reload()
  }

  return {
    // Modal state
    locationSelectorModalIsOpen,
    openLocationSelectorModal,
    closeLocationSelectorModal,
    // Access selection state
    accessSelected,
    accessAllowed,
    accessSelector,
    // Methods
    toggleItem,
    areAllSelected,
    selectAll,
    saveAccessSelection
  }
}
